<script setup>
import LazyPictureTag from '@/components/LazyPictureTag.vue'
import { computed, ref, watch } from 'vue'
import { formatPrice, vatPrice } from '../../../../../../Greativ/helper/price'
import { currency, refreshBasketItems } from '@/stores/basket'
import Button from '@/components/Button.vue'
import _clone from 'lodash/clone'
import axios from 'axios'
import { csrfToken } from '../../../../../../Greativ/global/csrf'
import { useMediaQuery, useTimeoutFn } from '@vueuse/core'

const props = defineProps({
  product: Object,
  locked: {
    type: Boolean,
    default: false,
  },
})
const isLargeScreen = useMediaQuery('(min-width: 1024px)')

const image = computed(() => {
  if (props.product.images.length) {
    return props.product.images[0].path
  }
  return ''
})

const formattedPrice = computed(() => {
  let price = 0
  if (props.product.client_price) {
    price = props.product.client_price
  } else if (props.product.discount_price) {
    price = props.product.discount_price
  } else {
    price = props.product.price
  }
  return formatPrice(vatPrice(price, props.product.vat))
})
const addToCardLoading = ref(false)
const locked = computed(() => {
  return props.locked || addToCardLoading.value
})
const amount = ref(0)
const addToCardAmountCounter = ref(0)
const addToCardSuccess = ref(false)
watch(
  () => props.product,
  (product, oldProduct) => {
    if (product.total_inventory === oldProduct?.total_inventory) {
      return
    }
    amount.value = product.total_inventory >= 1 ? 1 : 0
  },
  {
    immediate: true,
  }
)
const changeAmount = (value) => {
  let newValue = _clone(amount.value)
  if (isNaN(newValue)) {
    newValue = 1
  }
  const step = props.product.amount_step || 1

  if (value === 'inc') {
    newValue += step
  } else if (value === 'dec') {
    newValue -= step
  } else if (step && newValue % step !== 0) {
    newValue += step - (newValue % step)
  }

  let min = props.product.min_amount || 1
  if (step && min % step !== 0) {
    min += step - (min % step)
  }

  let max = props.product.max_amount || props.product.total_inventory
  max = max - addToCardAmountCounter.value

  if (step && max % step !== 0) {
    max -= max % step
  }

  if (newValue < min) {
    newValue = min
  }

  if (newValue > max) {
    newValue = max
  }
  amount.value = newValue
}
const { start: startAddedSuccessfullyToCardTimer } = useTimeoutFn(
  () => {
    addToCardSuccess.value = false
  },
  2000,
  {
    immediate: false,
  }
)
const addToBasket = async () => {
  if (parseInt(amount.value) < 1) {
    return false
  }
  addToCardLoading.value = true
  addToCardSuccess.value = false
  const {
    data: { success },
  } = await axios.post(
    'get/api/template/basket/addItem',
    {
      productId: props.product.id,
      amount: amount.value,
    },
    {
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      },
    }
  )

  if (success) {
    await refreshBasketItems()
    amount.value = 1
    changeAmount(1)
    addToCardSuccess.value = true
    addToCardLoading.value = false
    startAddedSuccessfullyToCardTimer()
  }
}
</script>
<template>
  <div
    class="flex items-center border-0 border-b-2px border-solid border-gray-700 last:border-b-0"
  >
    <div class="mr-10 w-56 flex-shrink-0 py-3">
      <div class="image relative pb-100%">
        <LazyPictureTag
          fallbackImage="ui/template/rallyestonia/images/no-image-placeholder-with-space.svg"
          :fallbackImageWidth="140"
          :fallbackImageHeight="140"
          :fallbackImageAlt="product.name"
          loadingImage="ui/template/rallyestonia/images/no-image-placeholder-with-space.svg"
          class="absolute inset-0 m-auto h-full !max-h-100% w-full !max-w-100% object-contain"
          :sizes="
            image
              ? [
                  {
                    mediaRule: 300,
                    url: image,
                    config: {
                      w: 160,
                      h: 160,
                      fit: 'max',
                    },
                  },
                ]
              : []
          "
        />
      </div>
    </div>

    <div
      class="flex-grow-1 lg:flex-center flex flex-col items-start lg:flex-row lg:items-center"
    >
      <div class="text-7 font-black">
        {{ product.name }}
        <div class="text-7 font-black text-red">
          {{ formattedPrice }}
          {{ currency }}
        </div>
      </div>

      <div class="mt-3 flex-shrink-0 py-3 lg:ml-auto lg:mt-0 lg:w-80 lg:px-10">
        <div
          class="relative z-2 flex w-9/12 items-stretch justify-center lg:mx-auto 2xl:w-8/12"
        >
          <div class="relative">
            <button
              type="button"
              class="focus:outline-none absolute left-4 top-0 bottom-0 inline-flex items-center border-none bg-transparent p-0 shadow-none"
              :disabled="locked"
              @click="changeAmount('dec')"
            >
              <svg
                class="h-8 w-8 fill-current text-red transition-colors duration-300 hover:text-red-900"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path
                  fill="currentColor"
                  d="M416 208H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"
                ></path>
              </svg>
            </button>

            <button
              type="button"
              class="focus:outline-none absolute right-4 top-0 bottom-0 inline-flex items-center border-none bg-transparent p-0 shadow-none"
              :disabled="locked"
              @click="changeAmount('inc')"
            >
              <svg
                class="h-8 w-8 fill-current text-red transition-colors duration-300 hover:text-red-900"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path
                  fill="currentColor"
                  d="M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z"
                ></path>
              </svg>
            </button>
            <input
              type="text"
              class="focus:outline-none block h-18 w-full border-none bg-gray pb-3px text-center text-7 font-black"
              inputmode="numeric"
              :disabled="locked"
              v-model="amount"
              @change="changeAmount($event.target.value)"
            />
          </div>
          <button
            type="button"
            class="focus:outline-none hover:btn-red-900 inline-flex w-20 flex-shrink-0 items-center justify-center border-none bg-red transition-colors duration-300 selected:bg-green"
            :class="{ selected: addToCardSuccess }"
            :disabled="locked"
            @click="addToBasket"
            v-if="!isLargeScreen"
          >
            <svg
              v-if="addToCardLoading"
              class="w-10 animate-spin fill-current text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-10 fill-current text-white"
              viewBox="0 0 20 20"
              fill="currentColor"
              v-else-if="addToCardSuccess"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>

            <svg
              x="0px"
              y="0px"
              viewBox="0 0 555.9 555.9"
              xml:space="preserve"
              class="w-10 fill-current text-white"
              v-else
            >
              <g>
                <path
                  d="M413.1,212.92L300.9,44.62c-5.1-5.1-12.75-10.2-20.4-10.2s-15.3,2.55-20.4,10.2l-112.2,168.3H25.5 c-15.3,0-25.5,10.2-25.5,25.5c0,2.55,0,5.1,0,7.65l63.75,237.15c5.1,20.4,25.5,38.25,48.45,38.25h331.5 c22.95,0,43.35-15.3,48.45-38.25l63.75-237.15c0-2.55,0-5.1,0-7.65c0-15.3-10.2-25.5-25.5-25.5L413.1,212.92L413.1,212.92z M204,212.92l76.5-112.2l76.5,112.2H204z"
                />
              </g>
            </svg>
          </button>
        </div>
      </div>

      <div class="ml-[7.6rem] flex-shrink-0" v-if="isLargeScreen">
        <button
          type="button"
          class="relative focus:outline-none hover:btn-red-900 inline-flex flex-shrink-0 items-center justify-center border-none bg-red transition-colors duration-300 selected:bg-green text-white h-18 pl-24 pr-8 uppercase font-bold text-[1.9rem]"
          :class="{ selected: addToCardSuccess }"
          :disabled="locked"
          @click="addToBasket"
        >
          <svg
            v-if="addToCardLoading"
            class="w-10 animate-spin fill-current text-white absolute inset-0 m-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-10 fill-current text-white absolute inset-0 m-auto"
            viewBox="0 0 20 20"
            fill="currentColor"
            v-else-if="addToCardSuccess"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>

          <svg
            x="0px"
            y="0px"
            viewBox="0 0 555.9 555.9"
            xml:space="preserve"
            class="w-10 fill-current text-white absolute left-8 top-0 bottom-0 m-auto"
            v-else
          >
            <g>
              <path
                d="M413.1,212.92L300.9,44.62c-5.1-5.1-12.75-10.2-20.4-10.2s-15.3,2.55-20.4,10.2l-112.2,168.3H25.5 c-15.3,0-25.5,10.2-25.5,25.5c0,2.55,0,5.1,0,7.65l63.75,237.15c5.1,20.4,25.5,38.25,48.45,38.25h331.5 c22.95,0,43.35-15.3,48.45-38.25l63.75-237.15c0-2.55,0-5.1,0-7.65c0-15.3-10.2-25.5-25.5-25.5L413.1,212.92L413.1,212.92z M204,212.92l76.5-112.2l76.5,112.2H204z"
              />
            </g>
          </svg>

          <span :class="{ 'opacity-0': addToCardLoading || addToCardSuccess }">
            {{ __('extra_product.btn_add_to_basket') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
